import React, { useState, useEffect } from 'react';
import { partnershipAPI } from '../services/api';

// ── Public /partner-interest — "Become a DWI Partner" ──────────────────────────
// Expression-of-interest ONLY: general information + a form. No specific
// project/site/pricing is ever shown here, and nothing here lets a visitor
// select a site or fund anything — DWI reviews every lead and privately
// invites the ones it chooses (see the admin Leads tab in Partnership.jsx).
export default function PartnerInterest() {
  const [statusLoading, setStatusLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', area: '', approxContribution: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    partnershipAPI.leadStatus()
      .then(r => setEnabled(r.enabled !== false))
      .catch(() => setEnabled(true))
      .finally(() => setStatusLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.area.trim()) {
      setErr('Full name, phone, and area/location are required.');
      return;
    }
    setSubmitting(true);
    setErr('');
    try {
      await partnershipAPI.submitLead(form);
      setSubmitted(true);
    } catch (e2) {
      setErr(e2.response?.data?.message || e2.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    page:  { maxWidth: 520, margin: '0 auto', minHeight: '100vh', background: 'var(--bg-page, #0a1628)' },
    hero:  { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', padding: '32px 20px', color: '#fff', textAlign: 'center' },
    title: { fontSize: 22, fontWeight: 800, margin: '0 0 8px' },
    sub:   { fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: 0 },
    body:  { padding: '24px 20px' },
    card:  { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, marginBottom: 20 },
    p:     { fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.7, margin: '0 0 10px' },
    label: { fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4, marginTop: 14 },
    input: { width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
             border: '1px solid var(--border)', background: 'var(--bg-page)',
             color: 'var(--text-primary)', fontSize: 14, fontFamily: 'inherit' },
    textarea: { width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
             border: '1px solid var(--border)', background: 'var(--bg-page)',
             color: 'var(--text-primary)', fontSize: 14, fontFamily: 'inherit', minHeight: 80, resize: 'vertical' },
    btn:   { width: '100%', marginTop: 22, padding: '13px 0', borderRadius: 10,
             background: '#22d3ee', color: '#0f172a', fontWeight: 700, fontSize: 15,
             border: 'none', cursor: 'pointer' },
    btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
    errBox: { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 14 },
    successBox: { background: 'var(--bg-card)', border: '2px solid #22d3ee', borderRadius: 14, padding: 20, textAlign: 'center' },
    successTitle: { fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px' },
  };

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <h1 style={styles.title}>Become a DWI Partner</h1>
        <p style={styles.sub}>Register your interest — our team will contact you.</p>
      </div>

      <div style={styles.body}>
        {statusLoading ? (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</p>
        ) : submitted ? (
          <div style={styles.successBox}>
            <p style={styles.successTitle}>Thank you</p>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0 }}>
              Our team will review and contact you.
            </p>
          </div>
        ) : !enabled ? (
          <div style={styles.card}>
            <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px' }}>Not accepting new interest right now</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
              Thanks for checking — please check back later.
            </p>
          </div>
        ) : (
          <>
            <div style={styles.card}>
              <p style={styles.p}>
                DWI is expanding reliable community internet through local partnerships.
                DWI supplies and manages the equipment and network — partners help fund and
                locally run a site and share in its revenue.
              </p>
              <p style={{ ...styles.p, margin: 0 }}>
                Register your interest below and our team will contact you.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <label style={styles.label}>Full name *</label>
              <input style={styles.input} type="text" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />

              <label style={styles.label}>Phone *</label>
              <input style={styles.input} type="tel" placeholder="+256 7XX XXX XXX" value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />

              <label style={styles.label}>Email</label>
              <input style={styles.input} type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />

              <label style={styles.label}>Area / location you're interested in *</label>
              <input style={styles.input} type="text" placeholder="e.g. Mukono" value={form.area}
                onChange={e => setForm(f => ({ ...f, area: e.target.value }))} />

              <label style={styles.label}>Approximate amount you could contribute</label>
              <input style={styles.input} type="text" placeholder="e.g. 3-5M UGX" value={form.approxContribution}
                onChange={e => setForm(f => ({ ...f, approxContribution: e.target.value }))} />

              <label style={styles.label}>Tell us why you're interested (optional)</label>
              <textarea style={styles.textarea} value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />

              {err && <div style={styles.errBox}>{err}</div>}

              <button type="submit" style={{ ...styles.btn, ...(submitting ? styles.btnDisabled : {}) }} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Register Interest'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
